import type { PromoCandidate } from '@supply-mover-2-0/shared';
import type { JSX } from 'react';
import { PromoTile } from './PromoTile';

/**
 * Auto-grid of all selected promos — the Weee!-style "all deals" tile.
 * `columns` controls desktop width; mobile renders 3 cols so a 6-promo
 * set lays out as 3×2 instead of stacking 2×3 (which on a phone burned
 * three full screen-folds for what's meant to be a "browse all offers"
 * affordance). Tablet (sm/md) bumps to 4 to fill the wider gutter
 * without going to the admin-configured desktop count too early.
 */
export function PromoGridWidget({
	promos,
	columns,
	density,
	onLikelyStaleUrl,
}: {
	promos: Array<PromoCandidate>;
	columns?: number;
	density?: 'standard' | 'compact';
	/** Forwarded to each PromoTile. See `PromoTile.onLikelyStaleUrl`. */
	onLikelyStaleUrl?: () => void;
}): JSX.Element | null {
	if (promos.length === 0) return null;
	// Tailwind needs static class names, so we map the admin-selected
	// column count to a known set rather than constructing the class
	// string dynamically (which the JIT compiler would not see).
	const desktopColsClass = (() => {
		switch (columns) {
			case 1:
				return 'lg:grid-cols-1';
			case 2:
				return 'lg:grid-cols-2';
			case 4:
				return 'lg:grid-cols-4';
			case 5:
				return 'lg:grid-cols-5';
			case 6:
				return 'lg:grid-cols-6';
			case 7:
				return 'lg:grid-cols-7';
			case 8:
				return 'lg:grid-cols-8';
			case 3:
			default:
				return 'lg:grid-cols-3';
		}
	})();
	return (
		<div
			className={`grid grid-cols-3 sm:grid-cols-4 md:grid-cols-4 ${desktopColsClass} gap-2 sm:gap-3 w-full h-full`}
		>
			{promos.map((p) => (
				<PromoTile
					key={p.id}
					promo={p as never}
					size="compact"
					density={density}
					onLikelyStaleUrl={onLikelyStaleUrl}
				/>
			))}
		</div>
	);
}
